import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag, RotateCcw, ThumbsDown, ThumbsUp } from 'lucide-react';
import styles from './TutorChat.module.css';
import { CopyButton } from './TutorMarkdown';
import {
  submitTutorFeedback,
  TutorFeedbackPayload,
  TutorFeedbackReason,
} from './tutorFeedback';

const REASONS: TutorFeedbackReason[] = ['wrong', 'unclear', 'off_topic', 'too_easy', 'too_hard', 'unsafe'];

interface Props {
  /**
   * Server-assigned message id. Feedback is disabled (with a tooltip) until it
   * exists, because the endpoint keys on the persisted message.
   */
  messageId?: string;
  /** Teaching session, sent along as context when known. */
  sessionId?: string | null;
  /** Plain markdown source copied to the clipboard. */
  text: string;
  onRegenerate?: () => void;
}

type Panel = null | 'down' | 'report';
type SendState = 'idle' | 'sending' | 'sent' | 'error';

/**
 * Copy / thumbs / try again / report row under a finished Ako reply.
 * Feedback goes to the real tutor feedback endpoint via tutorFeedback.ts.
 */
const MessageActions: React.FC<Props> = ({ messageId, sessionId, text, onRegenerate }) => {
  const { t } = useTranslation();
  const canRate = !!messageId;
  const [vote, setVote] = useState<null | 'up' | 'down'>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [reasons, setReasons] = useState<TutorFeedbackReason[]>([]);
  const [comment, setComment] = useState('');
  const [sendState, setSendState] = useState<SendState>('idle');
  const lastPayload = useRef<TutorFeedbackPayload | null>(null);
  const panelRef = useRef<HTMLFormElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (panel) panelRef.current?.querySelector<HTMLElement>('input,textarea')?.focus();
  }, [panel]);

  const closePanel = () => {
    setPanel(null);
    setReasons([]);
    setComment('');
    triggerRef.current?.focus();
  };

  /** Returns true on success. Failures never surface raw errors. */
  const send = async (payload: TutorFeedbackPayload): Promise<boolean> => {
    if (!messageId) return false;
    const full = { ...payload, ...(sessionId ? { sessionId } : {}) };
    lastPayload.current = full;
    setSendState('sending');
    try {
      await submitTutorFeedback(messageId, full);
      if (mounted.current) setSendState('sent');
      return true;
    } catch {
      if (mounted.current) setSendState('error');
      return false;
    }
  };

  const retrySend = () => {
    if (lastPayload.current) void send(lastPayload.current);
  };

  const onUp = () => {
    if (!canRate || vote === 'up') return;
    setVote('up'); // optimistic
    setPanel(null);
    void send({ rating: 'up' });
  };

  const openPanel = (p: Exclude<Panel, null>, e: React.MouseEvent<HTMLButtonElement>) => {
    if (!canRate) return;
    triggerRef.current = e.currentTarget;
    if (p === 'down') setVote('down');
    setSendState('idle');
    setPanel(p);
  };

  const submitPanel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!panel || sendState === 'sending') return;
    const ok = await send({
      rating: 'down',
      reasons: panel === 'down' ? (reasons.length ? reasons : undefined) : ['other'],
      comment: comment.trim() || undefined,
    });
    if (ok && mounted.current) closePanel();
  };

  const disabledTip = t('tutor.feedback_unavailable');
  const tip = (label: string) => (canRate ? label : disabledTip);

  const toggleReason = (r: TutorFeedbackReason) =>
    setReasons((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));

  return (
    <div className={styles.actions}>
      <div className={styles.actionRow} role="toolbar" aria-label={t('tutor.actions_label')}>
        <CopyButton text={text} label={t('tutor.copy_message')} className={styles.actionBtn} />
        <button
          type="button"
          className={styles.actionBtn}
          aria-pressed={vote === 'up'}
          aria-label={t('tutor.helpful')}
          title={tip(t('tutor.helpful'))}
          disabled={!canRate}
          onClick={onUp}
        >
          <ThumbsUp size={14} aria-hidden />
        </button>
        <button
          type="button"
          className={styles.actionBtn}
          aria-pressed={vote === 'down'}
          aria-expanded={panel === 'down'}
          aria-label={t('tutor.not_helpful')}
          title={tip(t('tutor.not_helpful'))}
          disabled={!canRate}
          onClick={(e) => openPanel('down', e)}
        >
          <ThumbsDown size={14} aria-hidden />
        </button>
        {onRegenerate && (
          <button type="button" className={styles.actionBtn} aria-label={t("tutor.retry")} onClick={onRegenerate}>
            <RotateCcw size={14} aria-hidden />
            <span className={styles.btnLabel}>{t("tutor.retry")}</span>
          </button>
        )}
        <button
          type="button"
          className={styles.actionBtn}
          aria-expanded={panel === 'report'}
          aria-label={t('tutor.report')}
          title={tip(t('tutor.report'))}
          disabled={!canRate}
          onClick={(e) => openPanel('report', e)}
        >
          <Flag size={14} aria-hidden />
        </button>
        {/* One live region carries the outcome: visible text, announced once. */}
        <span role="status" className={sendState === 'sent' ? styles.thanks : styles.srOnly}>
          {sendState === 'sent' ? t('tutor.thanks') : ''}
        </span>
        {sendState === 'error' && !panel && (
          <span className={styles.feedbackError} role="alert">
            {t('tutor.feedback_failed')}{' '}
            <button type="button" className={styles.linkBtn} onClick={retrySend}>{t('tutor.retry')}</button>
          </span>
        )}
      </div>

      {panel && (
        <form
          ref={panelRef}
          className={styles.feedbackPanel}
          onSubmit={submitPanel}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              closePanel();
            }
          }}
          aria-label={panel === 'down' ? t('tutor.feedback_title') : t('tutor.report_title')}
        >
          <p className={styles.panelTitle}>
            {panel === 'down' ? t('tutor.feedback_title') : t('tutor.report_title')}
          </p>
          {panel === 'down' && (
            <div className={styles.reasonList}>
              {REASONS.map((r) => (
                <label key={r} className={styles.reason}>
                  <input
                    type="checkbox"
                    checked={reasons.includes(r)}
                    onChange={() => toggleReason(r)}
                  />
                  <span>{t(`tutor.reason.${r}`)}</span>
                </label>
              ))}
            </div>
          )}
          <textarea
            className={styles.panelText}
            rows={2}
            value={comment}
            maxLength={1000}
            onChange={(e) => setComment(e.target.value)}
            aria-label={t('tutor.comment_label')}
            placeholder={panel === 'down' ? t('tutor.comment_placeholder') : t('tutor.report_placeholder')}
          />
          {sendState === 'error' && (
            <p className={styles.feedbackError} role="alert">{t('tutor.feedback_failed')}</p>
          )}
          <div className={styles.panelButtons}>
            <button type="submit" className={styles.primaryBtn} disabled={sendState === 'sending'}>
              {sendState === 'error' ? t('tutor.retry') : t('tutor.send_feedback')}
            </button>
            <button type="button" className={styles.ghostBtn} onClick={closePanel}>{t('tutor.cancel')}</button>
          </div>
        </form>
      )}
    </div>
  );
};

export default MessageActions;
